/**
 * Version history (reviews, checks, releases, deployments) read from the versioning service, in plain words. Pure:
 * no Angular, no HTTP — `VersionHistoryService` does the calls and the pages only show what these return.
 *
 * Shapes follow taf_versioningservice's DTOs (TAF.Versioning.Contracts/Responses), merged across origin/dev,
 * feat/ci-results, feat/review-previews and feat/environment-policy-and-versions. The service answers camelCase.
 *
 * The git host is never shown: every `toX` below copies only the fields a page uses, so the service's web addresses
 * (`mrWebUrl`, `commitWebUrl`, `webUrl`, `packageUrl`) never reach a template.
 */

// ---- keys ----------------------------------------------------------------------------------------------------------

const KEY_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;
const DASHED = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const PLAIN = /^[0-9a-f]{32}$/;

function guidHex(id: unknown): string | null {
  let s = typeof id === 'string' ? id.trim().toLowerCase() : '';
  if (s.startsWith('{') && s.endsWith('}')) s = s.slice(1, -1);
  if (!DASHED.test(s) && !PLAIN.test(s)) return null;
  return s.replace(/-/g, '');
}

/**
 * The service's space and repository keys for a tenant and an app, exactly as TAF Studio derives them
 * (taf_studio common/tafVersioningKeys.ts): `t-` / `a-` + the GUID's 32 hex digits, lowercase. Null when either id is
 * not a GUID — then there is no repository to ask about.
 */
export function versioningKeys(tenantId: unknown, appId: unknown): { space: string; repo: string } | null {
  const t = guidHex(tenantId);
  const a = guidHex(appId);
  if (!t || !a) return null;
  const space = `t-${t}`;
  const repo = `a-${a}`;
  return KEY_RE.test(space) && KEY_RE.test(repo) ? { space, repo } : null;
}

// ---- who is signed in ------------------------------------------------------------------------------------------------

/**
 * The `sub` of a JWT (with or without a `Bearer ` prefix): the id the versioning service knows a person by
 * (`userRef`, `submittedBy`, `startedBy`). Null when the token cannot be read.
 */
export function jwtSubject(token: unknown): string | null {
  if (typeof token !== 'string') return null;
  const raw = token.trim().replace(/^bearer\s+/i, '');
  const part = raw.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(
      Array.from(atob(padded), c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
    );
    const sub = JSON.parse(json)?.sub;
    return typeof sub === 'string' && sub.trim() ? sub.trim() : null;
  } catch {
    return null;
  }
}

/** The three version roles the service grants (`MemberRoles`). */
export type VersionRole = 'Maker' | 'Reviewer' | 'ReleaseManager';
const ROLES: readonly string[] = ['Maker', 'Reviewer', 'ReleaseManager'];

/** The roles `me` holds, from the `GET members` rows. Ids compare without case, like the service. */
export function rolesOf(members: unknown, me: string | null | undefined): Set<VersionRole> {
  const out = new Set<VersionRole>();
  const who = (me ?? '').trim().toLowerCase();
  if (!who || !Array.isArray(members)) return out;
  for (const m of members) {
    if (String(m?.userRef ?? '').trim().toLowerCase() === who && ROLES.includes(m?.role)) out.add(m.role);
  }
  return out;
}

/**
 * "You" for the signed-in person, else the display name the service sent with the id (`...Name`, filled in by the
 * versioning service from Identity when it is set up to), else the id itself.
 */
export function personLabel(userRef: unknown, me: string | null | undefined, name?: string | null): string {
  const id = typeof userRef === 'string' ? userRef.trim() : '';
  if (!id) return 'Unknown';
  if (me && id.toLowerCase() === me.trim().toLowerCase()) return 'You';
  const shown = typeof name === 'string' ? name.trim() : '';
  return shown || id;
}

/** The first 8 characters of a commit id, or an empty string. */
export function shortSha(sha: unknown): string {
  return typeof sha === 'string' ? sha.trim().slice(0, 8) : '';
}

// ---- reading the service's answers ---------------------------------------------------------------------------------

const str = (v: unknown): string => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v));
const optStr = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const list = (v: unknown): any[] => (Array.isArray(v) ? v : []);

export interface ReviewSummary {
  id: string;
  number: number;
  branch: string;
  title: string;
  submittedBy: string;
  /** The submitter's display name; null when the service sent none (then show the id). */
  submittedByName: string | null;
  state: string;
  createdAt: string;
}

export interface ReviewApproval {
  userRef: string;
  userName: string | null;
  decision: string;
  at: string;
  headSha: string | null;
}

export interface ReviewComment {
  id: string;
  userRef: string;
  userName: string | null;
  path: string;
  jsonPath: string | null;
  body: string;
  at: string;
}

export interface ReviewPreview {
  /** `creating`, `ready`, `failed` or `dropped`. */
  state: string;
  environmentId: string | null;
  error: string | null;
  updatedAt: string;
}

export interface ReviewDetail extends ReviewSummary {
  targetBranch: string;
  description: string | null;
  workItem: string | null;
  mergedSha: string | null;
  approvals: ReviewApproval[];
  comments: ReviewComment[];
  mergeStatus: string;
  pipelineStatus: string;
  headSha: string | null;
  preview: ReviewPreview | null;
}

export function toReviewSummary(raw: any): ReviewSummary {
  return {
    id: str(raw?.id),
    number: Number(raw?.mrIid) || 0,
    branch: str(raw?.branch),
    title: str(raw?.title),
    submittedBy: str(raw?.submittedBy),
    submittedByName: optStr(raw?.submittedByName),
    state: str(raw?.state),
    createdAt: str(raw?.createdAt)
  };
}

export function toReviewDetail(raw: any): ReviewDetail {
  const p = raw?.preview;
  return {
    ...toReviewSummary(raw),
    targetBranch: str(raw?.targetBranch),
    description: optStr(raw?.description),
    workItem: optStr(raw?.workItem),
    mergedSha: optStr(raw?.mergedSha),
    approvals: list(raw?.approvals).map(a => ({
      userRef: str(a?.userRef),
      userName: optStr(a?.userName),
      decision: str(a?.decision),
      at: str(a?.at),
      headSha: optStr(a?.headSha)
    })),
    comments: list(raw?.comments).map(c => ({
      id: str(c?.id),
      userRef: str(c?.userRef),
      userName: optStr(c?.userName),
      path: str(c?.path),
      jsonPath: optStr(c?.jsonPath),
      body: str(c?.body),
      at: str(c?.at)
    })),
    mergeStatus: str(raw?.mergeStatus),
    pipelineStatus: str(raw?.pipelineStatus),
    headSha: optStr(raw?.headSha),
    preview: p && typeof p === 'object'
      ? { state: str(p.state), environmentId: optStr(p.environmentId), error: optStr(p.error), updatedAt: str(p.updatedAt) }
      : null
  };
}

export interface CiCheck {
  job: string;
  status: string;
  jobId: string;
  pipelineId: string;
  updatedAt: string;
}

export interface CiRuleResult {
  job: string;
  ruleId: string;
  severity: string;
  status: string;
  filePath: string | null;
  message: string;
}

export interface CiResults {
  sha: string;
  blocking: boolean;
  checks: CiCheck[];
  results: CiRuleResult[];
  /** The review's head pipeline: id and status (`none`, `pending`, `running`, `success`, `failed`, `canceled`). */
  pipeline: { id: string | null; status: string; sha: string | null } | null;
}

export function toCiResults(raw: any): CiResults {
  const p = raw?.pipeline;
  return {
    sha: str(raw?.sha),
    blocking: raw?.blocking === true,
    checks: list(raw?.checks).map(c => ({
      job: str(c?.job),
      status: str(c?.status),
      jobId: str(c?.jobId),
      pipelineId: str(c?.pipelineId),
      updatedAt: str(c?.updatedAt)
    })),
    results: list(raw?.results).map(r => ({
      job: str(r?.job),
      ruleId: str(r?.ruleId),
      severity: str(r?.severity),
      status: str(r?.status),
      filePath: optStr(r?.filePath),
      message: str(r?.message)
    })),
    pipeline: p && typeof p === 'object' ? { id: optStr(p.id), status: str(p.status), sha: optStr(p.sha) } : null
  };
}

export interface Release {
  version: string;
  sha: string;
  createdBy: string;
  createdAt: string;
}

export function toRelease(raw: any): Release {
  return { version: str(raw?.version), sha: str(raw?.sha), createdBy: str(raw?.createdBy), createdAt: str(raw?.createdAt) };
}

export interface VersionTag {
  version: string;
  sha: string;
  prerelease: boolean;
}

export function toVersionTag(raw: any): VersionTag {
  return { version: str(raw?.version), sha: str(raw?.sha), prerelease: raw?.prerelease === true };
}

export interface Deployment {
  id: string;
  environment: string;
  version: string;
  /** `awaiting-approval`, `pending`, `running`, `succeeded`, `failed` or `canceled`. */
  status: string;
  startedBy: string;
  /** The requester's display name; null when the service sent none. */
  startedByName: string | null;
  startedAt: string;
  finishedAt: string | null;
  pipelineId: string | null;
  failureReason: string | null;
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
}

export function toDeployment(raw: any): Deployment {
  return {
    id: str(raw?.id),
    environment: str(raw?.environment),
    version: str(raw?.version),
    status: str(raw?.status),
    startedBy: str(raw?.startedBy),
    startedByName: optStr(raw?.startedByName),
    startedAt: str(raw?.startedAt),
    finishedAt: optStr(raw?.finishedAt),
    pipelineId: optStr(raw?.pipelineId),
    failureReason: optStr(raw?.failureReason),
    decidedBy: optStr(raw?.decidedBy),
    decidedByName: optStr(raw?.decidedByName),
    decidedAt: optStr(raw?.decidedAt)
  };
}

export interface EnvironmentState {
  environment: string;
  /** The newest succeeded deployment: what the environment runs. */
  current: Deployment | null;
  /** The newest deployment, whatever its status. */
  latest: Deployment | null;
}

export function toEnvironmentState(raw: any): EnvironmentState {
  return {
    environment: str(raw?.environment),
    current: raw?.current ? toDeployment(raw.current) : null,
    latest: raw?.latest ? toDeployment(raw.latest) : null
  };
}

export interface EnvironmentPolicy {
  environment: string;
  deployRole: string | null;
  requiresApproval: boolean;
  isProduction: boolean;
  isPreProduction: boolean;
  requiredPredecessor: string | null;
}

export function toEnvironmentPolicy(raw: any): EnvironmentPolicy {
  return {
    environment: str(raw?.environment),
    deployRole: optStr(raw?.deployRole),
    requiresApproval: raw?.requiresApproval === true,
    isProduction: raw?.isProduction === true,
    isPreProduction: raw?.isPreProduction === true,
    requiredPredecessor: optStr(raw?.requiredPredecessor)
  };
}

export interface PipelineJob {
  id: string;
  name: string;
  stage: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export function toPipelineJob(raw: any): PipelineJob {
  return {
    id: str(raw?.id),
    name: str(raw?.name),
    stage: str(raw?.stage),
    status: str(raw?.status),
    startedAt: optStr(raw?.startedAt),
    finishedAt: optStr(raw?.finishedAt)
  };
}

export interface HistoryEntry {
  sha: string;
  authorName: string;
  date: string;
  message: string;
}

export function toHistoryEntry(raw: any): HistoryEntry {
  return { sha: str(raw?.sha), authorName: str(raw?.authorName), date: str(raw?.date), message: str(raw?.message) };
}

/** One commit of a review (`ReviewCommitDto`); its page at the git host (`webUrl`) is never copied. */
export interface ReviewCommit {
  sha: string;
  title: string;
  authorName: string;
  date: string;
}

export function toReviewCommit(raw: any): ReviewCommit {
  return { sha: str(raw?.sha), title: str(raw?.title), authorName: str(raw?.authorName), date: str(raw?.date) };
}

// ---- plain words ---------------------------------------------------------------------------------------------------

/** How a status is drawn: a word and a colour family. */
export interface StatusView {
  label: string;
  tone: 'success' | 'danger' | 'warning' | 'info' | 'muted';
}

export function reviewStateView(state: string): StatusView {
  switch ((state ?? '').toLowerCase()) {
    case 'open': return { label: 'Waiting for review', tone: 'info' };
    case 'merged': return { label: 'Accepted', tone: 'success' };
    case 'closed': return { label: 'Closed without accepting', tone: 'muted' };
    default: return { label: state || 'Unknown', tone: 'muted' };
  }
}

/** The review's checks pipeline (`ReviewDetailDto.PipelineStatus`, `CiPipelineDto.Status`). */
export function pipelineStatusView(status: string): StatusView {
  switch ((status ?? '').toLowerCase()) {
    case 'success': return { label: 'Checks passed', tone: 'success' };
    case 'failed': return { label: 'Checks failed', tone: 'danger' };
    case 'running': return { label: 'Checks running', tone: 'warning' };
    case 'pending': return { label: 'Checks waiting to start', tone: 'warning' };
    case 'canceled': return { label: 'Checks cancelled', tone: 'muted' };
    case 'none': return { label: 'No checks ran', tone: 'muted' };
    case 'unknown': case '': return { label: 'Checks cannot be read right now', tone: 'muted' };
    default: return { label: status, tone: 'muted' };
  }
}

/** One CI check job (`CiCheckDto.Status`: pending, passed, failed, canceled). */
export function checkStatusView(status: string): StatusView {
  switch ((status ?? '').toLowerCase()) {
    case 'passed': return { label: 'Passed', tone: 'success' };
    case 'failed': return { label: 'Failed', tone: 'danger' };
    case 'pending': return { label: 'Running', tone: 'warning' };
    case 'canceled': return { label: 'Cancelled', tone: 'muted' };
    default: return { label: status || 'Unknown', tone: 'muted' };
  }
}

export function mergeStatusWords(status: string): string | null {
  switch ((status ?? '').toLowerCase()) {
    case '': case 'mergeable': return null;
    case 'conflict': return 'It conflicts with the main version.';
    case 'need-rebase': return 'It has to be brought up to date with the main version first.';
    case 'checking': return 'Still checking whether it can be accepted.';
    case 'blocked': return 'Accepting it is blocked.';
    case 'unknown': return 'Whether it can be accepted cannot be checked right now.';
    default: return `It cannot be accepted yet (${status}).`;
  }
}

/** A deployment or a pipeline job. */
export function deploymentStatusView(status: string): StatusView {
  switch ((status ?? '').toLowerCase()) {
    case 'succeeded': return { label: 'Deployed', tone: 'success' };
    case 'failed': return { label: 'Failed', tone: 'danger' };
    case 'running': return { label: 'Deploying', tone: 'warning' };
    case 'pending': return { label: 'Starting', tone: 'warning' };
    case 'awaiting-approval': return { label: 'Waiting for approval', tone: 'info' };
    case 'canceled': return { label: 'Cancelled', tone: 'muted' };
    default: return { label: status || 'Unknown', tone: 'muted' };
  }
}

/** Why the service ended a deployment itself (`DeploymentDto.FailureReason`). */
export function failureReasonWords(reason: string | null): string | null {
  switch ((reason ?? '').toLowerCase()) {
    case '': return null;
    case 'abandoned': return 'It stopped reporting progress and was given up.';
    case 'refused': return 'The deployment could not be started.';
    case 'pipeline-missing': return 'Its deployment run could no longer be found.';
    case 'rejected': return 'It was rejected before it started.';
    default: return reason;
  }
}

export interface PreviewView {
  label: string;
  tone: StatusView['tone'];
  detail: string | null;
  canOpen: boolean;
}

/** The review's preview (feat/review-previews): only `ready` can be opened. */
export function previewView(preview: ReviewPreview | null): PreviewView | null {
  if (!preview) return null;
  switch (preview.state.toLowerCase()) {
    case 'ready':
      return preview.environmentId
        ? { label: 'Ready', tone: 'success', detail: null, canOpen: true }
        : { label: 'Ready', tone: 'warning', detail: 'The preview is ready but did not say where it runs.', canOpen: false };
    case 'creating':
      return { label: 'Being prepared', tone: 'warning', detail: preview.error, canOpen: false };
    case 'failed':
      return { label: 'Could not be prepared', tone: 'danger', detail: preview.error, canOpen: false };
    case 'dropped':
      return { label: 'Removed', tone: 'muted', detail: 'Previews are removed once a review is accepted or closed.', canOpen: false };
    default:
      return { label: preview.state || 'Unknown', tone: 'muted', detail: preview.error, canOpen: false };
  }
}

/**
 * The address that opens this app on a preview's environment: the app's own address with `?environmentId=` and
 * `&previewReview=` — the parameters TAF Studio's "Open preview" uses and the app reads at start-up. Null without a
 * GUID environment id. A review number that is not a positive whole number is left out.
 */
export function previewAppUrl(origin: string, appCode: string, environmentId: string | null, review?: number): string | null {
  const env = (environmentId ?? '').trim().toLowerCase();
  if (!DASHED.test(env) || !appCode) return null;
  const params = new URLSearchParams({ environmentId: env });
  if (Number.isInteger(review) && (review as number) > 0) params.set('previewReview', String(review));
  return `${origin.replace(/\/+$/, '')}/${encodeURIComponent(appCode)}?${params}`;
}

/** Reviews as the list shows them: open first, newest first within each group. */
export function reviewOrder(reviews: readonly ReviewSummary[]): ReviewSummary[] {
  return [...reviews].sort((a, b) => {
    const oa = a.state.toLowerCase() === 'open' ? 0 : 1;
    const ob = b.state.toLowerCase() === 'open' ? 0 : 1;
    return oa - ob || b.createdAt.localeCompare(a.createdAt);
  });
}

// ---- review decisions and changes ----------------------------------------------------------------------------------

/** Each person's latest decision, keyed by lower-case id. */
export function latestDecisions(approvals: readonly ReviewApproval[]): Map<string, ReviewApproval> {
  const out = new Map<string, ReviewApproval>();
  const sorted = [...approvals].sort((a, b) => a.at.localeCompare(b.at));
  for (const a of sorted) out.set(a.userRef.toLowerCase(), a);
  return out;
}

/**
 * Approvals that count: the latest decision of each person other than the author is `approve`, made on the review's
 * current head (a change after an approval makes it stop counting). An approval sent without a head counts.
 */
export function countingApprovals(detail: Pick<ReviewDetail, 'approvals' | 'submittedBy' | 'headSha'>): number {
  const author = detail.submittedBy.toLowerCase();
  let n = 0;
  for (const [who, a] of latestDecisions(detail.approvals)) {
    if (who === author || a.decision !== 'approve') continue;
    if (a.headSha === null || (detail.headSha && a.headSha.toLowerCase() === detail.headSha.toLowerCase())) n++;
  }
  return n;
}

export function changesRequested(detail: Pick<ReviewDetail, 'approvals'>): boolean {
  return [...latestDecisions(detail.approvals).values()].some(a => a.decision === 'request-changes');
}

/**
 * Whether the signed-in person may approve or ask for changes. The service lets a Reviewer decide on an open review
 * (`RepositoryAction.Review`); this page also keeps the author from approving their own.
 */
export function reviewDecisionAllowed(
  detail: Pick<ReviewDetail, 'state' | 'submittedBy'>,
  me: string | null,
  roles: ReadonlySet<VersionRole>
): boolean {
  if (detail.state.toLowerCase() !== 'open' || !me || !roles.has('Reviewer')) return false;
  return detail.submittedBy.toLowerCase() !== me.toLowerCase();
}

export interface ChangedFile {
  path: string;
  kind: 'added' | 'removed' | 'changed';
}

/**
 * The files a branch changes against its target, from the two trees (`GET tree?ref=`): added, removed, or a
 * different blob. Sorted by path.
 */
export function changedFiles(
  base: readonly { path: string; blobSha: string }[],
  head: readonly { path: string; blobSha: string }[]
): ChangedFile[] {
  const before = new Map(base.map(f => [f.path, f.blobSha]));
  const after = new Map(head.map(f => [f.path, f.blobSha]));
  const out: ChangedFile[] = [];
  for (const [path, blob] of after) {
    if (!before.has(path)) out.push({ path, kind: 'added' });
    else if (before.get(path) !== blob) out.push({ path, kind: 'changed' });
  }
  for (const path of before.keys()) if (!after.has(path)) out.push({ path, kind: 'removed' });
  return out.sort((a, b) => a.path.localeCompare(b.path));
}

/** A package path (`metadata/screens/Tasks.json`) as "Tasks (screens)". */
export function fileLabel(path: string): string {
  const parts = path.split('/').filter(Boolean);
  const file = (parts.pop() ?? path).replace(/\.json$/i, '');
  const folder = parts.length ? parts[parts.length - 1] : '';
  return folder ? `${file} (${folder})` : file;
}

// ---- releases and deployments --------------------------------------------------------------------------------------

/** Whether the person may start a deployment to `environment`: ReleaseManager, plus the environment's own role. */
export function canDeployTo(environment: string, roles: ReadonlySet<VersionRole>, policies: readonly EnvironmentPolicy[]): boolean {
  if (!roles.has('ReleaseManager')) return false;
  const policy = policies.find(p => p.environment.toLowerCase() === environment.toLowerCase());
  return !policy?.deployRole || roles.has(policy.deployRole as VersionRole);
}

/** Whether the person may approve a deployment waiting for approval (never their own). */
export function canApproveDeployment(
  d: Deployment,
  me: string | null,
  roles: ReadonlySet<VersionRole>,
  policies: readonly EnvironmentPolicy[]
): boolean {
  if (d.status !== 'awaiting-approval' || !me || d.startedBy.toLowerCase() === me.toLowerCase()) return false;
  return canDeployTo(d.environment, roles, policies);
}

/** Whether the person may reject a deployment waiting for approval (its requester may withdraw it). */
export function canRejectDeployment(
  d: Deployment,
  me: string | null,
  roles: ReadonlySet<VersionRole>,
  policies: readonly EnvironmentPolicy[]
): boolean {
  if (d.status !== 'awaiting-approval' || !me) return false;
  return d.startedBy.toLowerCase() === me.toLowerCase() || canDeployTo(d.environment, roles, policies);
}

/**
 * The environment after `environment` on the promotion path, or null (last, not on the path, or no path set).
 */
export function nextEnvironment(environment: string, path: readonly string[]): string | null {
  const i = path.findIndex(e => e.toLowerCase() === environment.toLowerCase());
  return i >= 0 && i + 1 < path.length ? path[i + 1] : null;
}

/**
 * The version a roll-back of `environment` goes to: the newest earlier succeeded deployment there of a version other
 * than the one it runs now. `history` is newest first, as the service lists it. Null when there is none.
 */
export function rollbackTarget(environment: string, current: string | null, history: readonly Deployment[]): string | null {
  for (const d of history) {
    if (d.environment.toLowerCase() !== environment.toLowerCase() || d.status !== 'succeeded') continue;
    if (current && d.version === current) continue;
    return d.version;
  }
  return null;
}

/**
 * Whether a production environment would refuse the version (feat/environment-policy-and-versions: prerelease versions
 * never reach production).
 */
export function isPrerelease(version: string): boolean {
  return /-/.test(version);
}

/**
 * Compares two versions (`X.Y.Z` or `X.Y.Z-rc.N`) for sorting newest first: a negative number when `a` is newer. A
 * release candidate sorts below its release; anything unreadable sorts last, by text.
 */
export function compareVersionsNewestFirst(a: string, b: string): number {
  const parse = (v: string) => {
    const m = /^v?(\d+)\.(\d+)\.(\d+)(?:-(.+))?$/.exec(v.trim());
    return m ? { n: [Number(m[1]), Number(m[2]), Number(m[3])], pre: m[4] ?? null } : null;
  };
  const pa = parse(a);
  const pb = parse(b);
  if (!pa || !pb) return pa ? -1 : pb ? 1 : b.localeCompare(a);
  for (let i = 0; i < 3; i++) if (pa.n[i] !== pb.n[i]) return pb.n[i] - pa.n[i];
  if (pa.pre === pb.pre) return 0;
  if (pa.pre === null) return -1;
  if (pb.pre === null) return 1;
  return pb.pre.localeCompare(pa.pre, undefined, { numeric: true });
}

/** One released version as the releases table shows it. */
export interface ReleaseRow {
  version: string;
  prerelease: boolean;
  createdAt: string;
  createdBy: string;
  sha: string;
}

/**
 * The released versions: the service's releases (with who made them and when) joined with its version tags (which
 * say whether a version is a release candidate), newest version first. A tag without a release still shows.
 */
export function releaseRows(releases: readonly Release[], versions: readonly VersionTag[]): ReleaseRow[] {
  const rows = new Map<string, ReleaseRow>();
  for (const r of releases) {
    rows.set(r.version, { version: r.version, prerelease: isPrerelease(r.version), createdAt: r.createdAt, createdBy: r.createdBy, sha: r.sha });
  }
  for (const v of versions) {
    const row = rows.get(v.version);
    if (row) row.prerelease = v.prerelease || row.prerelease;
    else rows.set(v.version, { version: v.version, prerelease: v.prerelease, createdAt: '', createdBy: '', sha: v.sha });
  }
  return [...rows.values()].sort((a, b) => compareVersionsNewestFirst(a.version, b.version));
}

// ---- errors --------------------------------------------------------------------------------------------------------

/** Raised by the page itself: no versioning service address is configured, so nothing was asked. */
export const VERSIONING_NOT_SET_UP = 'VERSIONING_NOT_SET_UP';

/** What went wrong with a call, read from an Angular HttpErrorResponse (or anything thrown). */
export interface VersioningFailure {
  status: number;
  code: string | null;
  message: string | null;
}

export function readFailure(error: any): VersioningFailure {
  if (error?.code === VERSIONING_NOT_SET_UP) return { status: 0, code: VERSIONING_NOT_SET_UP, message: null };
  const body = error?.error;
  const err = body && typeof body === 'object' ? body.error : null;
  return {
    status: typeof error?.status === 'number' ? error.status : 0,
    code: typeof err?.code === 'string' ? err.code : null,
    message: typeof err?.message === 'string' && err.message.trim() ? err.message : null
  };
}

/**
 * A failure in plain words. `what` names what was being read ("the reviews"). A 403 on a log says the Deploy
 * permission is needed, because that is the one the service asks for there.
 */
export function failureWords(error: any, what: string, needsDeploy = false): string {
  const f = readFailure(error);
  if (f.code === VERSIONING_NOT_SET_UP) return 'Version history is not set up on this TAF server yet.';
  if (f.status === 403) {
    return needsDeploy
      ? `You cannot see ${what}: that needs permission to deploy this app (the Release manager role in its version history). Ask whoever manages the app's version history.`
      : `You do not have permission to see ${what}.`;
  }
  if (f.status === 401) return 'Your session has ended. Sign in again to see ' + what + '.';
  if (f.status === 404) return f.code === 'VERSIONING_NOT_FOUND' || f.code === 'VERSIONING_NOT_CONNECTED'
    ? `This app has no version history yet, so there is nothing to show for ${what}.`
    : `${capitalise(what)} could not be found.`;
  if (f.status === 0) return `Could not reach the version history service to read ${what}. Check your connection and try again.`;
  if (f.status === 502 || f.status === 503) return `${capitalise(what)} cannot be read right now. Try again in a few minutes.`;
  return f.message ?? `${capitalise(what)} could not be read.`;
}

/** A refused action in plain words (409 conflicts carry the service's own sentence). */
export function actionFailureWords(error: any, action: string): string {
  const f = readFailure(error);
  if (f.code === VERSIONING_NOT_SET_UP) return 'Version history is not set up on this TAF server yet.';
  if (f.status === 403) return `You do not have permission to ${action}.`;
  if (f.status === 0) return `Could not reach the version history service to ${action}. Try again.`;
  return f.message ?? `Could not ${action}. Try again.`;
}

function capitalise(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
